import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { ArrowLeft, Clock, User } from "lucide-react";
import BlogCover from "@/components/BlogCover";

export default function BlogDetail() {
  const { slug } = useParams();
  const { data: blog, isLoading } = useQuery({
    queryKey: ["blog", slug],
    queryFn: async () => (await api.get(`/blogs/${slug}`)).data,
  });

  if (isLoading) return <div className="p-10 text-slate-500 font-mono-fin">Loading…</div>;
  if (!blog) return <div className="p-10 text-slate-500 font-mono-fin">Not found.</div>;

  const content = blog.content || "";
  // Treat as HTML if it looks like it (rich-editor output). Otherwise fall back to legacy markdown paragraphs.
  const isHtml = /<(p|h1|h2|h3|ul|ol|li|strong|em|a|blockquote|br)[\s>]/i.test(content);
  const paragraphs = isHtml ? [] : content.split("\n\n").map(p => p.trim()).filter(Boolean);

  // Crawlers need ABSOLUTE image URLs — cover_image may be a relative /api/uploads path.
  const SITE = "https://niveshstreet.in";
  const ogImage = blog.cover_image
    ? (blog.cover_image.startsWith("http") ? blog.cover_image : `${SITE}${blog.cover_image}`)
    : `${SITE}/og-social-card.jpg`;
  const ogDesc = (blog.excerpt || content.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ")).trim().slice(0, 200);

  return (
    <article className="px-4 sm:px-6 lg:px-10 py-8 max-w-[760px] mx-auto">
      <Helmet>
        <title>{`${blog.title} · Nivesh Street`}</title>
        <meta name="description" content={(blog.excerpt || content.replace(/<[^>]*>/g, "")).slice(0, 160)} />
        <meta name="author" content={blog.author || "Nivesh Street Desk"} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={ogDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:secure_url" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`https://niveshstreet.in/blogs/${blog.slug}`} />
        <meta property="article:section" content={blog.category || "Markets"} />
        <meta property="article:published_time" content={blog.created_at} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={ogDesc} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={`https://niveshstreet.in/blogs/${blog.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: blog.title,
          image: blog.cover_image ? [blog.cover_image] : undefined,
          datePublished: blog.created_at,
          dateModified: blog.updated_at || blog.created_at,
          author: [{ "@type": "Person", name: blog.author || "Nivesh Street Desk" }],
          publisher: {
            "@type": "Organization",
            name: "Nivesh Street",
            logo: { "@type": "ImageObject", url: "https://customer-assets-agu9un31.emergentagent.net/job_invest-pro-170/artifacts/jqc1m5xr_Logo.png" },
          },
          articleSection: blog.category,
          description: (blog.excerpt || "").slice(0, 200),
          mainEntityOfPage: `https://niveshstreet.in/blogs/${blog.slug}`,
        })}</script>
      </Helmet>

      <div className="flex justify-center mb-5">
        <Link to="/blogs" className="inline-flex items-center gap-2 text-[11px] font-mono-fin uppercase tracking-[0.25em] text-[#6B6C6F] hover:text-[#D91E2A]" data-testid="blog-back">
          <ArrowLeft className="w-3.5 h-3.5" /> All Insights
        </Link>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <span className="ns-eyebrow" style={{ fontSize: 11 }}>{(blog.category || "RESEARCH").toUpperCase()}</span>
        <span className="text-[11px] font-mono-fin text-[#8C8D90] inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {new Date(blog.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
        </span>
      </div>

      <h1 className="font-heading font-extrabold tracking-tight text-[#1A1A1A] mb-6 leading-[1.1] text-[clamp(2rem,5vw,3.25rem)]" data-testid="blog-title">
        {blog.title}
      </h1>

      <div className="text-sm text-[#1A1A1A] font-semibold mb-1 flex items-center gap-2">
        <User className="w-3.5 h-3.5 text-[#6B6C6F]" /> {blog.author}
      </div>
      <div className="text-xs text-[#6B6C6F] mb-6">
        Published {new Date(blog.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
      </div>

      {blog.cover_image && (
        <div className="rounded-md overflow-hidden mb-8 aspect-video">
          <BlogCover src={blog.cover_image} title={blog.title} category={blog.category} />
        </div>
      )}

      <div className="h-px bg-[#E2DDD3] mb-6" />

      {isHtml ? (
        <div
          className="ns-prose"
          data-testid="blog-body-html"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: content }}
        />
      ) : (
        <div className="ns-prose">
          {paragraphs.map((p, i) => {
            const key = `p-${i}-${p.slice(0, 20)}`;
            if (p.startsWith("## ")) return <h2 key={key}>{p.slice(3)}</h2>;
            if (p.startsWith("# ")) return <h2 key={key}>{p.slice(2)}</h2>;
            if (p.startsWith("- ")) {
              const items = p.split("\n").map(l => l.replace(/^-\s*/, ""));
              return <ul key={key}>{items.map((it) => <li key={`${key}-${it.slice(0,20)}`}>{it}</li>)}</ul>;
            }
            return <p key={key}>{p}</p>;
          })}
        </div>
      )}

      {blog.tags?.length > 0 && (
        <div className="mt-10 pt-6 border-t border-[#E2DDD3] flex flex-wrap gap-2">
          {blog.tags.map(t => (
            <span key={t} className="text-[10px] font-mono-fin uppercase tracking-wider px-2 py-1 rounded bg-[#D91E2A]/10 text-[#B8141F] border border-[#D91E2A]/25">#{t}</span>
          ))}
        </div>
      )}
    </article>
  );
}
